import React, { useRef, useState } from 'react';
import { CmsButton } from '../../types/contentstack';
import { CmsButtonLink } from '../CmsButtonLink/CmsButtonLink';
import PlayIcon from '../icons/PlayIcon';

interface CmsFile {
  url: string;
  description: string;
}

interface CmsHeroBannerItem {
  image: CmsFile | null;
  video?: CmsFile | null;
  headline: string;
  body: string;
  buttons: CmsButton[];
  image_position?: 'top' | 'bottom';
}

interface CmsHeroBannerCarouselData {
  name?: string;
  component?: string;
  items: CmsHeroBannerItem[];
}

interface CmsHeroBannerCarouselProps {
  data: CmsHeroBannerCarouselData;
  className?: string;
}

const HeroBannerCarouselItem: React.FC<CmsHeroBannerItem> = ({
  image,
  video,
  headline,
  body,
  buttons,
  image_position = 'bottom',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  };

  const showContentBlock = headline || body || buttons.length > 0;

  const contentSection = showContentBlock && (
    <section className="flex flex-col justify-center items-center text-center px-6 py-12">
      <h1 className="font-bold font-sans leading-action max-w-4xl mb-6 text-[#192026] text-[64px]">
        {headline}
      </h1>

      <p className="text-lg text-gray-700 leading-relaxed mb-8 max-w-[628px] text-gray-925">
        <p dangerouslySetInnerHTML={{ __html: body }} />
      </p>

      {buttons.length > 0 && (
        <div className="flex gap-4 flex-wrap justify-center">
          {buttons.map((cta) => (
            <CmsButtonLink
              key={`hero-btn-${cta.url}-${cta.text}`}
              text={cta.text}
              url={cta.url}
              external={cta.external}
              button_type={cta.button_type}
              icon={cta.icon}
              icon_position={cta.icon_position}
              className={`flex items-center gap-2 px-8 py-3 rounded-md font-semibold transition-colors ${
                cta.variant === 'outlined'
                  ? 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50'
                  : 'bg-[#0054A4] text-white hover:bg-blue-700'
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );

  const videoSection = video?.url && (
    <section className="pb-6">
      <div className="relative group cursor-pointer overflow-hidden rounded-xl">
        <div ref={containerRef} className="relative w-full aspect-video">
          <video
            ref={videoRef}
            src={video?.url}
            poster={image?.url ?? undefined}
            disablePictureInPicture
            playsInline
            preload="metadata"
            className="absolute inset-0 w-full h-full object-cover"
            onPlay={() => {
              setIsPlaying(true);
            }}
            onPause={() => {
              setIsPlaying(false);
            }}
            onContextMenu={(e) => e.preventDefault()}
            onClick={togglePlayPause}
          >
            <track kind="captions" />
          </video>
          {!isPlaying && (
            <button
              onClick={togglePlayPause}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/70 px-125 transition-all z-20"
              aria-label={isPlaying ? 'Pause video' : 'Play video'}
            >
              <PlayIcon />
            </button>
          )}
        </div>
      </div>
    </section>
  );

  const imageSection = !video?.url && image?.url && (
    <section className="pb-6">
      <div className="flex justify-center cursor-pointer">
        <img src={image.url} alt={image?.description} className="w-full max-w-[1280px] max-h-[720px] rounded-xl object-cover" />
      </div>
    </section>
  );

  return (
    <div className="w-full overflow-hidden">
      {image_position === 'top' ? (
        <>
          {videoSection}
          {imageSection}
          {contentSection}
        </>
      ) : (
        <>
          {contentSection}
          {videoSection}
          {imageSection}
        </>
      )}
    </div>
  );
};

export const CmsHeroBannerCarousel: React.FC<CmsHeroBannerCarouselProps> = ({
  data,
  className = '',
}) => {
  return (
    <div className={`w-full py-12 px-6 ${className}`}>
      <div className="mx-auto max-w-7xl">
        {data.items[0] && <HeroBannerCarouselItem {...data.items[0]} />}
      </div>
    </div>
  );
};

export default CmsHeroBannerCarousel;
